import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input, OnChanges, OnDestroy, OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {DndDropEvent, DndModule} from "ngx-drag-drop";
import {Backend} from "@generated-backend-types";
import {AttachmentsApiService} from "./services/attachments-api.service";
import {BehaviorSubject, Observable, Subject, switchMap} from "rxjs";
import {ContextMenuComponent, ContextMenuModule} from "@perfectmemory/ngx-contextmenu";
import {map} from "rxjs/operators";
import {BsModalRef, BsModalService} from "ngx-bootstrap/modal";
import {FormsModule, NonNullableFormBuilder, ReactiveFormsModule} from "@angular/forms";
import {ContextMenuActionEnum} from "./ContextMenuAction.enum";
import {UploaderService} from "./services/uploader.service";
import {LocaleService} from "../../../../services/locale.service";
import {Locale} from "date-fns";
import Attachment = Backend.App.Http.Resources.Dashboard.Attachment;
import AttachmentData = Backend.App.Data.Attachments.AttachmentData;
import AttachmentTypeEnum = Backend.App.Enums.AttachmentTypeEnum;
import {CoreModule} from "../../../core.module";
import {AngularSplitModule} from "angular-split";
import {FolderComponent} from "./folder/folder.component";
import {FormatInTimeZonePipe} from "../../pipes/format-in-time-zone.pipe";
import {FileSizePipe} from "../../pipes/filesize.pipe";
import {DateFnsModule} from "ngx-date-fns";
import {FormGroupComponent} from "../form-inputs/form-group/form-group.component";
import {ClipboardModule} from "ngx-clipboard";
import {TooltipModule} from "ngx-bootstrap/tooltip";

@Component({
    selector: 'app-file-manager',
    templateUrl: './file-manager.component.html',
    imports: [
      CoreModule,
      AngularSplitModule,
      ContextMenuModule,
      FolderComponent,
      DndModule,
      FormsModule,
      ReactiveFormsModule,
      FormatInTimeZonePipe,
      FileSizePipe,
      DateFnsModule,
      FormGroupComponent,
      ClipboardModule,
      TooltipModule,
    ],
    standalone: true
})
export class FileManagerComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('folderContextMenu') public folderContextMenu?: ContextMenuComponent<Attachment>;
  @ViewChild('newDirectoryModalTemplate') public newDirectoryModalTemplate?: TemplateRef<BsModalRef>;
  @ViewChild('renameAttachmentModalTemplate') public renameAttachmentModalTemplate?: TemplateRef<BsModalRef>;
  @ViewChild('previewAttachmentModalTemplate') public previewAttachmentModalTemplate?: TemplateRef<BsModalRef>;
  @ViewChild('fileContextMenu') public fileContextMenu?: ContextMenuComponent<Attachment>;
  @ViewChild('backgroundContextMenu') public backgroundContextMenu?: ContextMenuComponent<string>;
  @ViewChild('fileInput') public fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('directoryInput') public directoryInput!: ElementRef<HTMLInputElement>;

  @Input() public uploadEndpoint?: string;

  @Output() public filesSelected: EventEmitter<Attachment[]> = new EventEmitter<Attachment[]>()
  @Output() public requestClose: EventEmitter<void> = new EventEmitter<void>()
  @Input() public limitSelectToOneFile = false
  @Input() public limitToImages = false
  @Input() public selectionEnabled = true
  public selected: Attachment[] = []
  public showPreview: Subject<Attachment> = new Subject<Attachment>()
  previewAttachment?: Backend.App.Http.Resources.Dashboard.Attachment;
  public locale: "de" | "en";
  public dfnsLocale: Locale


  @HostListener('window:keydown.shift', ['$event'])
  public shiftDown() {
    this.keyPressedShift = true;
  }

  @HostListener('window:keydown.space', ['$event'])
  public spaceDown() {
    if (this.selected.length === 1 && !this.previewAttachment)  {
      this.showPreview.next(this.selected[0])
    }
    else if (this.previewAttachment) {
      this.previewAttachmentModal?.hide()
    }
  }


  @HostListener('window:keyup.shift', ['$event'])
  public shiftUp() {
    this.keyPressedShift = false;
  }


  @HostListener('window:keydown.meta', ['$event'])
  public controlDown() {
    this.keyPressedMeta = true;
  }

  @HostListener('window:keyup.meta', ['$event'])
  public controlUp() {
    this.keyPressedMeta = false;
  }

  @HostListener('window:keydown.meta.a', ['$event'])
  public metaADown(event: KeyboardEvent) {
    event.preventDefault()
    this.selectAll()
  }


  @HostListener('window:keydown.delete', ['$event'])
  public delete() {
    if (this.selected) {
      this.selected.map(
        a => this.deleteAttachment(a)
      )

    }
  }

  @HostListener('window:keydown.backspace', ['$event'])
  public backspace() {
    if (this.selected) {
      this.selected.map(
        a => this.deleteAttachment(a)
      )
    }
  }


  public currentDragAttachment?: Backend.App.Http.Resources.Dashboard.Attachment;
  private currentUploadPath = '/';
  private attachmentInRenameModal?: Backend.App.Http.Resources.Dashboard.Attachment;
  private lastClickedWithoutShiftAttachmentIndex = 0;
  public keyPressedShift = false;
  public keyPressedMeta = false;
  public allSelected = false;

  public newDirectoryModal?: BsModalRef;
  public renameAttachmentModal?: BsModalRef<Attachment>;
  public previewAttachmentModal?: BsModalRef<Attachment>;

  public newDirectoryForm = this.fb.group({
    name: this.fb.control<string>(''),
    path: this.fb.control<string>('/'),
  })

  public renameAttachmentForm = this.fb.group({
    name: this.fb.control<string>(''),
    path: this.fb.control<string>(''),
  })


  public sidebarVisible = true;

  public attachments: Subject<Attachment[]> = new Subject<Backend.App.Http.Resources.Dashboard.Attachment[]>();
  public attachmentsByPath: BehaviorSubject<{ [key: string]: Attachment[] }> = new BehaviorSubject({});


  public isSelected(t: Attachment): boolean {
    return this.selected.findIndex(att => att.id === t.id) > -1
  }

  public currentPath: BehaviorSubject<string> = new BehaviorSubject<string>('/');
  public triggerPathLoad: Subject<string> = new Subject<string>();
  public pathLoaded: Subject<{ path: string, attachments: Attachment[] }> = new Subject<{
    path: string,
    attachments: Attachment[]
  }>();
  public root: Backend.App.Http.Resources.Dashboard.Attachment = {
    created_at: "",
    image_height: null,
    image_width: null,
    mime_type: null,
    name: "",
    path: "/",
    size: null,
    type: AttachmentTypeEnum.DIRECTORY,
    updated_at: "",
    id: 'root',
    url: '',
  };

  public ngOnInit() {
    if(this.uploadEndpoint) this.attachmentsService.setEndpoint(this.uploadEndpoint);
  }

  public ngOnChanges() {
    if(this.uploadEndpoint) this.attachmentsService.setEndpoint(this.uploadEndpoint);
  }

  public ngOnDestroy() {
    this.attachmentsService.resetToDefaultEndpoint();
  }


  deleteAttachment(value: Attachment) {
    this.lastClickedWithoutShiftAttachmentIndex = 0;
    this.attachmentsService.delete(value.id).subscribe(
      () => {
        this.selected = [];

        const current = this.attachmentsByPath.value[value.path];

        current.splice(current.findIndex(a => a.id === value.id), 1);
        this.pathLoaded.next({path: value.path, attachments: current});
      },
      (error) => {
        alert(error.error.message);
      }
    )
  }


  loadPath(path: string) {
    this.triggerPathLoad.next(path)
  }

  reload() {
    this.currentPath.next(this.currentPath.value)
  }

  emitClose() {
    this.requestClose.emit()
  }

  openFolder(attachment: Attachment) {
    if (attachment.type === AttachmentTypeEnum.FILE) {
      return;
    }
    let path;
    if (attachment.path === "/") {
      path = attachment.path + attachment.name
    } else {
      path = attachment.path + "/" + attachment.name
    }
    if (path === '//') {
      path = '/'
    }
    this.openPath(path)
  }

  executeAttachmentContextMenu(action: ContextMenuActionEnum, event: {
    event: MouseEvent | KeyboardEvent;
    value?: Attachment;
  }) {
    if (!event.value) {
      return;
    }

    switch (action) {
      case ContextMenuActionEnum.DELETE:
        this.deleteAttachment(event.value)
        break;
      case ContextMenuActionEnum.NEW_DIRECTORY:
        this.addDirectory(event.value?.path)
        break
      case ContextMenuActionEnum.RENAME:
        this.rename(event.value)
        break
      case ContextMenuActionEnum.UPLOAD_FILE:
        this.uploadFile(event.value.path)
        break

      case ContextMenuActionEnum.UPLOAD_DIRECTORY:
        this.uploadDirectory(event.value.path)
        break


    }

  }

  executePathContextMenu(action: ContextMenuActionEnum, event: {
    event: MouseEvent | KeyboardEvent;
    value?: string;
  }) {


    if (!event.value) {
      return;
    }

    switch (action) {
      case ContextMenuActionEnum.NEW_DIRECTORY:
        this.addDirectory(event.value)
        break
      case ContextMenuActionEnum.UPLOAD_FILE:
        this.uploadFile(event.value)
        break

      case ContextMenuActionEnum.UPLOAD_DIRECTORY:
        this.uploadDirectory(event.value)
        break
    }

  }


  onDragStart(event: DragEvent, attachment: Attachment) {
    this.currentDragAttachment = attachment;
  }


  addDirectory(path: string) {
    this.newDirectoryForm.controls.path.setValue(path);
    if (this.newDirectoryModalTemplate) {
      this.newDirectoryModal = this.modalService.show(this.newDirectoryModalTemplate,
        {
          ignoreBackdropClick: false,
          keyboard: true,
          backdrop: true,
        }
      )
      this.newDirectoryModal.onHide?.subscribe(
        () => {
          this.newDirectoryForm.setValue({
            name: '',
            path: '/'
          })
        }
      )
    }
  }

  fileSelected(event: Event) {

    const target = event.target as HTMLInputElement;


    if (target?.files) {
      this.uploaderService.uploadFiles({files: target.files, path: this.currentUploadPath}
      );

    }

  }

  uploadFile(path = this.currentPath.value) {
    this.currentUploadPath = path;
    this.fileInput.nativeElement.click()
  }

  uploadDirectory(path = this.currentPath.value) {
    this.currentUploadPath = path;
    this.directoryInput.nativeElement.click()
  }

  rename(attachment: Attachment) {

    this.attachmentInRenameModal = attachment;
    this.renameAttachmentForm.setValue({name: attachment.name, path: attachment.path})
    if (this.renameAttachmentModalTemplate) {
      this.renameAttachmentModal = this.modalService.show(this.renameAttachmentModalTemplate,
        {
          initialState: attachment,
          ignoreBackdropClick: true,
          keyboard: true,
          backdrop: true,
        }
      )
      this.renameAttachmentModal.onHide?.subscribe(
        () => {
          this.loadPath(attachment.path);
          this.renameAttachmentForm.reset()
        }
      )
    }
  }

  storeDirectory() {
    if (!this.newDirectoryForm.valid) {
      return;
    }
    this.attachmentsService.create(this.newDirectoryForm.value as AttachmentData).pipe(
      map(() => {
        this.loadPath(this.newDirectoryForm.controls.path.value)
      }),
    ).subscribe(
      () => {
        this.newDirectoryModal?.hide()

      }
    )
  }

  renameAttachment() {
    if (!this.renameAttachmentForm.valid || !this.attachmentInRenameModal?.id) {
      return;
    }
    this.attachmentsService.update(this.attachmentInRenameModal.id, this.renameAttachmentForm.value).pipe(
      map(() => {
        this.loadPath(this.newDirectoryForm.controls.path.value)
      }),
    ).subscribe(
      () => {
        this.renameAttachmentModal?.hide()

      }
    )
  }

  public openInNewTab(url: string | null): void{
    if (url) window.open(url)
  }


  onDragEnd() {
    this.selected = [];
    this.currentDragAttachment = undefined;
  }

  checkIfAllSelected() {
    this.allSelected = this.selected.length >= this.attachmentsByPath.value[this.currentPath.value]?.filter(a => a.type === AttachmentTypeEnum.FILE).length
  }

  toggleSelectAll() {

    if (this.allSelected) {
      this.selected = []
      this.allSelected = false;
    } else {
      this.selectAll()

    }


  }

  selectAll() {
    if (this.limitSelectToOneFile) {
      return;
    }
    this.selected = this.attachmentsByPath.value[this.currentPath.value].filter(a => a.type === AttachmentTypeEnum.FILE)
    this.allSelected = true;
  }

  onClick(attachment: Attachment, index: number): void {

    if (attachment.type !== AttachmentTypeEnum.FILE) {
      return;
    }
    if (this.limitToImages && !this.isImage(attachment)) {
      return
    }


    if (this.keyPressedShift && !this.limitSelectToOneFile) {

      // markiere alle attachments zwischen lastClicked und index
      this.selected = this.attachmentsByPath.value[this.currentPath.value].filter(
        (a, idx) => {
          return (idx >= this.lastClickedWithoutShiftAttachmentIndex && idx <= index)
            || (idx >= index && idx <= this.lastClickedWithoutShiftAttachmentIndex)
        });

    } else if (this.keyPressedMeta && !this.limitSelectToOneFile) {
      if (this.selected.includes(attachment)) {
        this.selected = this.selected.filter(a => a.id !== attachment.id);
      } else {
        this.selected.push(attachment)
      }
    }
    if (this.limitSelectToOneFile) {
      if (this.selected[0]?.id === attachment.id) {
        this.selected = []
      } else {
        this.selected = [attachment]
      }
    } else if (!this.keyPressedShift && !this.keyPressedMeta) {
      this.selected = [attachment];
    }

    if (!this.keyPressedShift) {
      this.lastClickedWithoutShiftAttachmentIndex = index
    }
    this.checkIfAllSelected()

  }

  onDoubleClick(attachment: Attachment, event: MouseEvent): boolean {
    event.preventDefault()
    if (attachment.type === AttachmentTypeEnum.DIRECTORY) {
      this.openFolder(attachment)
    }

    if (attachment.type === AttachmentTypeEnum.FILE) {

      this.showPreview.next(attachment);

    }

    return true
  }

  openPath(path: string): void {
    this.currentPath.next(path)
  }

  onDragCanceled() {
    this.currentDragAttachment = undefined;
  }


  dragOverHandler(event: DragEvent) {
    event.preventDefault();
  }

  onFileDropped(event: DragEvent, path = this.currentPath.value) {
    // Prevent default behavior (Prevent file from being opened)
    if (event.dataTransfer?.files) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.uploaderService.uploadFiles(
        {files: event.dataTransfer.files, path: path}
      );
    }
    if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
      // no files?
    }


  }

  onDndDrop(event: DndDropEvent, targetPath: string = this.currentPath.getValue()) {
    targetPath = targetPath.replace('//', '/');

    const attachment: Attachment = event.data;

    this.attachmentsService.update(attachment.id, {
      name: attachment.name, path: targetPath
    }).subscribe(
      (a) => {
        this.loadPath(a.data.path);
        this.loadPath(attachment.path);
      }
    )


  }

  private loadFromApi(path: string): Observable<Attachment[]> {
    if (path === '//') {
      path = '/'
    }
    return this.attachmentsService.getPage(1, {path})
      .pipe(
        map((d) => {
          this.pathLoaded.next({path: path, attachments: d.data})
          return d.data
        })
      )
  }


  public isImage(attachment: Attachment): boolean {
    return attachment.image_width !== null && attachment.image_height !== null
  }

  public sortByNameAlpha(a: Attachment, b: Attachment) {
    const textA = a.name.toUpperCase();
    const textB = b.name.toUpperCase();
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  }

  public constructor(
    private readonly attachmentsService: AttachmentsApiService,
    public readonly uploaderService: UploaderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: BsModalService,
    private readonly fb: NonNullableFormBuilder,
    readonly localeService: LocaleService
  ) {

    this.locale = localeService.currentLocale
    this.dfnsLocale = localeService.getDfnsLocale()
    localeService.localeChanged.subscribe(
      l => {
        this.locale = l
        this.dfnsLocale = localeService.getDfnsLocale()
        this.cdr.markForCheck()
      }
    )
    this.showPreview.subscribe(
      (a) => {
        this.previewAttachment = a;
        if (this.previewAttachmentModalTemplate) {
          this.previewAttachmentModal = this.modalService.show(this.previewAttachmentModalTemplate,
            {
              initialState: a,
              ignoreBackdropClick: false,
              keyboard: true,
              backdrop: true,
            }
          )
          this.previewAttachmentModal.onHide?.subscribe(
            () => {
              this.previewAttachment = undefined
            }
          )
        }
      }
    )

    this.filesSelected.subscribe(
      () => {
        this.previewAttachmentModal?.hide()
      }
    )

    this.uploaderService.currentUploads.subscribe(
      () => {

        this.cdr.markForCheck()
      }
    )
    this.uploaderService.fileUploaded.subscribe(
      (upload) => {

        if (upload.attachment) {
          const current = this.attachmentsByPath.value[upload.path];
          current.push(upload.attachment);
          this.pathLoaded.next({path: upload.path, attachments: current});
        }

      }
    )

    this.pathLoaded.subscribe(
      (data) => {
        this.lastClickedWithoutShiftAttachmentIndex = 0;

        const sorted = data.attachments.sort(this.sortByNameAlpha);
        if (data.path === this.currentPath.value) {
          this.attachments.next(sorted)
        }
        this.attachmentsByPath.next({...this.attachmentsByPath.value, [data.path]: sorted});
        this.cdr.detectChanges()
        this.checkIfAllSelected()
      }
    )

    this.triggerPathLoad.subscribe(
      (path) => {
        this.loadFromApi(path).subscribe()

      }
    )

    this.currentPath.pipe(
      switchMap((path: string) => {
          this.selected = [];
          return this.loadFromApi(path)
        }
      ))
      .subscribe(
        () => {
          this.cdr.markForCheck()
        }
      )

  }

  protected readonly ContextMenuActionEnum = ContextMenuActionEnum;
  protected readonly Object = Object;
  protected readonly open = open;
}
